import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import usePlacesAutocomplete, {
  type Suggestion,
} from "use-places-autocomplete";

import { toastError } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import { patchDetails } from "~/v2/api/rest/account";
import type { SubscribeStepProps } from "../Subscribe.controller";
import { AdditionalInfoPresentation } from "./AdditionalInfo.pres";
import {
  type AdditionalInfoSchema,
  additionalInfoResolver,
  additionalInfoSchema,
} from "./schema";

export const AdditionalInfoConnector = ({
  account,
  details,
  country,
  onBackToPreviousStep,
  onContinueToNextStep,
}: SubscribeStepProps) => {
  const [isAddressFocused, setIsAddressFocused] = useState(false);

  const form = useForm<AdditionalInfoSchema>({
    resolver: zodResolver(
      additionalInfoSchema.superRefine(additionalInfoResolver),
    ),
    mode: "onSubmit",
    defaultValues: {
      given_name: details?.given_name ?? "",
      middle_name: details?.middle_name ?? "",
      family_name: details?.family_name ?? "",
      country_of_tax_residence: details?.country_of_tax_residence
        ? country.alpha3ToName(details?.country_of_tax_residence)
        : "",
      street_address: details?.street_address?.at(0) ?? "",
      city: details?.city ?? "",
      state: details?.state ?? "",
      postal_code: details?.postal_code ?? "",
      unit: details?.unit ?? "",
      phone_number: details?.phone_number ?? "",
      employment_status:
        (details?.employment_status as AdditionalInfoSchema["employment_status"]) ??
        undefined,
      employer: details?.employer ?? "",
      employer_address: details?.employer_address ?? "",
      position: details?.position ?? "",
      function: details?.function ?? "",
    },
  });

  const queryClient = useQueryClient();

  const mutation = useMutationRequest(
    (payload: AdditionalInfoSchema) =>
      patchDetails(account?.id ?? "", {
        ...payload,
        street_address: [payload.street_address],
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.accounts.details(account?.id ?? ""),
        });
        onContinueToNextStep();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error submitting additional info: ${errorMessage}`);
      },
    },
  );

  const onSubmitValid: SubmitHandler<AdditionalInfoSchema> = (data) => {
    mutation.mutate({
      ...data,
      country_of_tax_residence:
        country.nameToAlpha3(data.country_of_tax_residence) ?? "",
    });
  };

  const onSubmitInvalid: SubmitErrorHandler<AdditionalInfoSchema> = () =>
    toastError("Invalid form");

  const handleSubmit = form.handleSubmit(onSubmitValid, onSubmitInvalid);

  const {
    ready: isUSAAddressSuggestionsReady,
    suggestions: usaAddressSuggestions,
    value: usaAddressSuggestionValue,
    setValue: setUSAAddressSuggestion,
  } = usePlacesAutocomplete({
    debounce: 50,
    defaultValue: details?.street_address?.at(0) ?? "",
    requestOptions: {
      strictBounds: true,
      componentRestrictions: { country: "usa" },
    },
  });

  const handleSetUSAAddressSuggestion = (suggestion: Suggestion) => {
    setUSAAddressSuggestion(suggestion);
    setIsAddressFocused(true);
  };

  const filteredUSAAddressSuggestions = usaAddressSuggestions?.data.filter(
    (value) =>
      // we only want specific types of places
      value.types.includes("street_address") || value.types.includes("premise"),
  );

  const isEmployerInfoRequired = form.watch("employment_status") === "EMPLOYED";
  const isInternational =
    country.nameToAlpha3(form.watch("country_of_tax_residence")) !== "USA";
  const isLoading = mutation.isPending || country.isLoading;

  const showAddressSuggestions =
    !isInternational &&
    isAddressFocused &&
    isUSAAddressSuggestionsReady &&
    filteredUSAAddressSuggestions.length > 0;

  const addressSuggestions = {
    isDisplayed: showAddressSuggestions,
    value: usaAddressSuggestionValue,
    suggestions: filteredUSAAddressSuggestions,
    setValue: handleSetUSAAddressSuggestion,
    onFocus: () => setIsAddressFocused(true),
    onClose: () => setIsAddressFocused(false),
  };

  const isGivenNameDisabled = !!details?.given_name;
  const isMiddleNameDisabled = !!details?.middle_name;
  const isFamilyNameDisabled = !!details?.family_name;

  return (
    <AdditionalInfoPresentation
      form={form}
      onBack={onBackToPreviousStep}
      onSubmit={handleSubmit}
      countries={country.allowedCountries}
      isEmployerInfoRequired={isEmployerInfoRequired}
      addressSuggestions={addressSuggestions}
      isLoading={isLoading}
      isGivenNameDisabled={isGivenNameDisabled}
      isMiddleNameDisabled={isMiddleNameDisabled}
      isFamilyNameDisabled={isFamilyNameDisabled}
    />
  );
};
